import {
	type ErrorResponse,
	Form,
	Link,
	isRouteErrorResponse,
	useParams,
	useRouteError,
	useLocation,
} from '@remix-run/react'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import { getErrorMessage } from '#app/utils/misc.tsx'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => JSX.Element | null

export function GeneralErrorBoundary({
	statusHandlers,
}: {
	statusHandlers?: Record<number, StatusHandler>
}) {
	const error = useRouteError()
	captureRemixErrorBoundaryError(error)
	const params = useParams()

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	if (
		isRouteErrorResponse(error) &&
		error.status === 403 &&
		error.data === 'Invalid CSRF token'
	) {
		return <InvalidCSRFMessage />
	}

	return (
		<>
			{isRouteErrorResponse(error) ? (
				(statusHandlers?.[error.status] ?? ResponseErrorMessage)({
					error,
					params,
				})
			) : (
				<UnexpectedErrorMessage error={error} />
			)}
		</>
	)
}

function UnexpectedErrorMessage({ error }: { error: unknown }) {
	console.error('Unexpected error', getErrorMessage(error))
	const pathname = useLocation().pathname

	return (
		<div className="container flex flex-col items-center justify-center gap-4 p-20">
			<p className="text-center text-h2">Something went wrong.</p>
			<div className="flex max-w-[65ch] flex-col gap-1 text-center text-lg">
				<p>
					Please{' '}
					<Link to={pathname} replace className="underline">
						refresh the page
					</Link>{' '}
					and re-try.
				</p>
				<div>
					If you're seeing this error again,{' '}
					<Form method="POST" action="/logout" className="inline">
						<button type="submit" className="underline">
							sign out
						</button>
					</Form>{' '}
					and log back into your account and re-try.
				</div>
				<Support />
			</div>
		</div>
	)
}

function ResponseErrorMessage({
	error,
	params,
}: React.ComponentProps<StatusHandler>) {
	console.error('Response error', error)

	if (error.status === 400) {
		return <BadRequestMessage error={error} params={params} />
	}
	if (error.status === 401) {
		return <UnauthorizedMessage error={error} params={params} />
	}
	if (error.status === 403) {
		return <ForbiddenMessage error={error} params={params} />
	}
	if (error.status === 404) {
		return <NotFoundMessage error={error} params={params} />
	}
	if (error.status >= 500) {
		return <ServerErrorMessage error={error} params={params} />
	}

	return <UnexpectedErrorMessage error={error} />
}

function BadRequestMessage({ error }: React.ComponentProps<StatusHandler>) {
	console.error('Not found error', error)

	return (
		<div className="container flex flex-col items-center justify-center gap-4 p-20">
			<p className="text-center text-h2">400 Bad Request</p>
			<div className="flex max-w-[65ch] flex-col gap-1 text-center text-lg">
				<p>
					Looks like you sent a bad request. Please go back to the{' '}
					<Link to="/" className="underline">
						home page
					</Link>{' '}
					and try again.
				</p>
				<Support />
			</div>
		</div>
	)
}

function UnauthorizedMessage({ error }: React.ComponentProps<StatusHandler>) {
	console.error('Not found error', error)

	return (
		<div className="container flex flex-col items-center justify-center gap-4 p-20">
			<p className="text-center text-h2">401 Unauthorized</p>
			<div className="flex max-w-[65ch] flex-col gap-1 text-center text-lg">
				<p>
					Looks like you are not logged in. Please{' '}
					<Link to="/login" className="underline">
						log in
					</Link>{' '}
					to continue.
				</p>
				<p>
					Try{' '}
					<Form method="POST" action="/logout" className="inline">
						<button type="submit" className="underline">
							signing out
						</button>
					</Form>{' '}
					and logging back in if you are already logged in.{' '}
				</p>
				<Support />
			</div>
		</div>
	)
}

function ForbiddenMessage({ error }: React.ComponentProps<StatusHandler>) {
	console.error('Not found error', error)

	return (
		<div className="container flex flex-col items-center justify-center gap-4 p-20">
			<p className="text-center text-h2">403 Forbidden</p>
			<div className="flex max-w-[65ch] flex-col gap-1 text-center text-lg">
				<p>
					Looks like you are not allowed to access this page. Please go back to
					the{' '}
					<Link to="/" className="underline">
						home page
					</Link>{' '}
					and try again.
				</p>
				<Support />
			</div>
		</div>
	)
}

function NotFoundMessage({ error }: React.ComponentProps<StatusHandler>) {
	console.error('Not found error', error)

	return (
		<div className="container flex flex-col items-center justify-center gap-4 p-20">
			<p className="text-center text-h2">404 Page not found</p>
			<div className="flex max-w-[65ch] flex-col gap-1 text-center text-lg">
				<p>
					Go back to the{' '}
					<Link to="/" className="underline">
						home page
					</Link>{' '}
					and try again.
				</p>
				<Support />
			</div>
		</div>
	)
}

function ServerErrorMessage({ error }: React.ComponentProps<StatusHandler>) {
	console.error('Not found error', error)
	const pathname = useLocation().pathname

	return (
		<div className="container flex flex-col items-center justify-center gap-4 p-20">
			<p className="text-center text-h2">
				Something went wrong and it's probably us.
			</p>
			<div className="flex max-w-[65ch] flex-col gap-1 text-center text-lg">
				<p>
					Please{' '}
					<Link to={pathname} replace className="underline">
						refresh the page
					</Link>{' '}
					and re-try again in a few minutes.
				</p>
				<p>
					If you're seeing this error again,{' '}
					<Form method="POST" action="/logout" className="inline">
						<button type="submit" className="underline">
							sign out
						</button>
					</Form>{' '}
					and log back into your account and re-try.
				</p>
				<Support />
			</div>
		</div>
	)
}

function InvalidCSRFMessage() {
	console.error('Invalid CSRF token')
	const pathname = useLocation().pathname

	return (
		<div className="container flex flex-col items-center justify-center gap-4 p-20">
			<h1 className="text-center text-h2">Invalid CSRF token.</h1>
			<div className="flex max-w-[65ch] flex-col gap-1 text-lg">
				<p>
					Please{' '}
					<Link to={pathname} replace className="underline">
						refresh the page
					</Link>{' '}
					and re-try your action.
				</p>
				<div>
					If you're seeing this error again, these solutions may help:
					<ol className="list-disc">
						<li>
							<Form method="POST" action="/logout" className="inline">
								<button type="submit" className="underline">
									Sign out
								</button>
							</Form>{' '}
							and log back into your account and re-try.
						</li>
						<li>
							Clear your cookies and cache for{' '}
							<a
								href="https://support.google.com/accounts/answer/32050"
								target="_blank"
								rel="noopener noreferrer"
								className="underline"
							>
								Google Chrome
							</a>
							,{' '}
							<a
								href="https://support.mozilla.org/en-US/kb/how-clear-firefox-cache"
								target="_blank"
								rel="noopener noreferrer"
								className="underline"
							>
								Mozilla Firefox
							</a>
							, or{' '}
							<a
								href="https://support.apple.com/en-us/HT201265"
								target="_blank"
								rel="noopener noreferrer"
								className="underline"
							>
								Safari
							</a>{' '}
							and re-try the action.
						</li>
					</ol>
					<Support />
				</div>
			</div>
		</div>
	)
}

function Support() {
	return (
		<p>
			If you think this is a mistake, please contact{' '}
			<Link to="mailto:arpitdalalm@gmail.com" className="underline">
				support
			</Link>
			.
		</p>
	)
}
